import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import SectionDetails from '../../_Sections/SectionDetails';
import SectionGallery from '../../_Sections/SectionGallery';
import SectionDropship from '../../_Sections/SectionDropship';
import SectionPairings from '../../_Sections/SectionPairings';
import SectionBranding from '../../_Sections/SectionBranding';
import SectionSizes from '../../_Sections/SectionSizes';
import { useSelector } from 'react-redux';
import WebsiteStore from '../../../WebsiteStore';
import { Box, Container, Grid, Stack, Typography, styled } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import SectionPricing from '../../_Sections/SectionPricing';
import tKey from '../../../Common/Utils/tKey';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import ProductSEO from '../../_Sections/ProductSEO';
import { BlackCheckedIcon } from '../../../Common/_components/CheckBoxes';
import SizeDisplay from '../../../Common/Measurements/SizeDisplay';

const PRODUCT_ID = 133;
const VIEW_PRODUCT_ID = 160;

const CustomWarning = styled('div')(({ theme }) => ({
  marginTop: 60,
  color: 'white',
  textAlign: 'center',
}))

export default function CottonSateenTableclothPage() {
  const [t] = useTranslation('page/products/cotton-sateen-tablecloths');
  const productData = useAppSelector(state => state.get('appData').get('products').get(String(PRODUCT_ID)));
  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'))

  if (!productData) return null

  return <div className="centered-page product-page-v4">
    <ProductSEO
      viewProductId={VIEW_PRODUCT_ID}
      title={t('Custom all-over print Cotton-Sateen Tablecloths | Products')}
      description={t('Design and create your own custom printed Cotton-Sateen Tablecloths featuring your artwork. ✔ Print-on-demand drop shipping ✔ No minimums ✔ Made in Canada')}
    />
    <SectionDetails
      viewProductId={VIEW_PRODUCT_ID}
      color="#9ff2ff"
      bgStyle={{
        backgroundPosition: 'center right',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '47%',
      }}
      fullPageBg
      tag={t('The all-over print')}
      title={t('Cotton-Sateen Tablecloth')}
      text={t('Celebrate your art naturally with custom printed cotton sateen tablecloths. Made from 200 thread count OEKO-TEX certified cotton, this smooth, soft fabric features vibrant, permanent prints. It’s a striking and practical way to bring your designs to life in a large, everyday format.')}
      createUrl={productData.get('url_slug')}
      img={require('@resources/img/pages/products/cotton-sateen-tablecloths/banner-1-' + locale + '.png')}
      alt={t('Picture of Cotton-Sateen Tablecloth')}
      details={[
        {
          img: require('@resources/img/pages/products/cotton-sateen-tablecloths/icon-1.svg?url'),
          alt: t('Cotton icon'),
          text: t('Made from 200 thread count OEKO-TEX certified cotton sateen fabric'),
        },
        {
          img: require('@resources/img/pages/products/cotton-sateen-tablecloths/icon-2.svg?url'),
          alt: t('Hem icon'),
          text: t('Professionally finished with double rolled hem'),
        },
        {
          img: require('@resources/img/pages/products/cotton-sateen-tablecloths/icon-3.svg?url'),
        alt: t('Table icon'),
          text: t('Sized for square and rectangle tables'),
        },
        {
          img: require('@resources/img/pages/products/cotton-sateen-tablecloths/icon-4.svg?url'),
          alt: t('Print icon'),
          text: t('Durable and vibrant permanent prints'),
        },
        {
          img: require('@resources/img/pages/products/cotton-sateen-tablecloths/icon-5.svg?url'),
          alt: t('Canada icon'),
          text: t('Digitally printed on demand in Montreal, Canada'),
        },
      ]}
    />
    <SectionGallery images={[
      require('@resources/img/pages/products/cotton-sateen-tablecloths/gallery_0.jpg'),
      require('@resources/img/pages/products/cotton-sateen-tablecloths/gallery_1.jpg'),
      require('@resources/img/pages/products/cotton-sateen-tablecloths/gallery_2.jpg'),
      require('@resources/img/pages/products/cotton-sateen-tablecloths/gallery_3.jpg'),
      require('@resources/img/pages/products/cotton-sateen-tablecloths/gallery_4.jpg'),
    ]} />

    <SectionDropship
      title={<Trans
        t={t}
        i18nKey={tKey('<yellow>Drop ship</yellow> your Cotton-Sateen Tablecloth<br/> <pink>on-demand</pink>')}
        components={{
          yellow: <span className="ty" />,
          pink: <span className="tp" />,
        }}
      />}
      text={t('Your brand and following will love Cotton-Sateen Tablecloth! At prices designed for reselling, Cotton-Sateen Tablecloth made on demand meet the needs of sellers who prefer not to stock inventory. Sell them in your eCommerce store and decide your profit margin')}
    />

    <SectionPricing
      productId={PRODUCT_ID}
      listOptions
      options={['60x60','90x60','cotton-sateen']}
    />

    <SectionSizes
      productId={PRODUCT_ID}
      title={t('Cotton-Sateen Tablecloth Sizing')}
      text={<Trans
        t={t}
        i18nKey={tKey('Available in 2 sizes: <sizeSquare/> and <sizeRectangle/>')}
        components={{
          sizeSquare: <SizeDisplay sizes={[60, 60]} sourceUnit="in" displayType="physical" />,
          sizeRectangle: <SizeDisplay sizes={[90, 60]} sourceUnit="in" displayType="physical" />,
        }}
      />}
    >
      <img
        src={require('@resources/img/pages/products/cotton-sateen-tablecloths/measurement.svg?url')}
        style={{
          marginRight: 0,
        }}
      />
      <CustomWarning>
        <p><Trans
          t={t}
          i18nKey={tKey('Due to variation in sewing, the final measurements may vary up to <variance/> in length or width on the finished product.')}
          components={{
            variance: <SizeDisplay sizes={[1]} sourceUnit="in" displayType="physical" />,
          }}
        /></p>
      </CustomWarning>
    </SectionSizes>

    <SectionBranding
      title={t('Brand your Cotton-Sateen Tablecloth')}
      text={t('Branding enhances your products. Add custom labels, artist cards or stickers to your packages to create a cohesive message and build your brand.')}
    />

    <SectionPairings
      text={t('Pair your Cotton-Sateen Tablecloth with these great products to create your own unique styles.')}
      productSlugs={['coaster', 'placemat', 'stainless-steel-tumbler']}
    />
  </div>
}
